// Hex grid math (docs/04-SIMULATION.md §2). Pure integer math only.
//
// Conventions
// - Flat-top hexes, axial coordinates (q, r). Cube coordinates add s = -q - r.
// - Level files use offset coordinates [col, row] in the "odd-q" layout:
//   odd columns sit half a hex lower than even columns.
// - Directions are numbered clockwise from north, as seen on screen (y grows downward):
//   0 N, 1 NE, 2 SE, 3 S, 4 SW, 5 NW. Facing uses the same numbers.
import type { Hex, Offset } from './types';
import { absInt, floorDiv } from './intmath';

export type Cube = { q: number; r: number; s: number };
export type Direction = 0 | 1 | 2 | 3 | 4 | 5;

/** Axial step for each direction, clockwise from north. */
export const DIRECTIONS: readonly Readonly<Hex>[] = Object.freeze([
  Object.freeze({ q: 0, r: -1 }), // 0 N
  Object.freeze({ q: 1, r: -1 }), // 1 NE
  Object.freeze({ q: 1, r: 0 }), // 2 SE
  Object.freeze({ q: 0, r: 1 }), // 3 S
  Object.freeze({ q: -1, r: 1 }), // 4 SW
  Object.freeze({ q: -1, r: 0 }), // 5 NW
]);

export const DIRECTION_NAMES = ['N', 'NE', 'SE', 'S', 'SW', 'NW'] as const;

export function hex(q: number, r: number): Hex {
  return { q, r };
}

export function hexEquals(a: Hex, b: Hex): boolean {
  return a.q === b.q && a.r === b.r;
}

/** Stable string key, e.g. for caches. */
export function hexKey(h: Hex): string {
  return `${h.q},${h.r}`;
}

/** Total order for sorting: by r, then q. */
export function compareHex(a: Hex, b: Hex): number {
  return a.r - b.r || a.q - b.q;
}

export function toCube(h: Hex): Cube {
  return { q: h.q, r: h.r, s: -h.q - h.r };
}

export function fromCube(c: Cube): Hex {
  return { q: c.q, r: c.r };
}

// ---------- offset (odd-q) <-> axial ----------

export function offsetToAxial(o: Offset): Hex {
  const [col, row] = o;
  return { q: col, r: row - (col - (col & 1)) / 2 };
}

export function axialToOffset(h: Hex): Offset {
  return [h.q, h.r + (h.q - (h.q & 1)) / 2];
}

// ---------- neighbors, distance, direction ----------

export function add(a: Hex, b: Hex): Hex {
  return { q: a.q + b.q, r: a.r + b.r };
}

export function subtract(a: Hex, b: Hex): Hex {
  return { q: a.q - b.q, r: a.r - b.r };
}

export function neighbor(h: Hex, dir: Direction): Hex {
  return add(h, DIRECTIONS[dir] as Hex);
}

/** All six neighbors in direction order 0..5. */
export function neighbors(h: Hex): Hex[] {
  return DIRECTIONS.map((d) => add(h, d));
}

export function distance(a: Hex, b: Hex): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (absInt(dq) + absInt(dr) + absInt(dq + dr)) / 2;
}

export function isAdjacent(a: Hex, b: Hex): boolean {
  return distance(a, b) === 1;
}

/**
 * Direction index from `a` toward `b`: exact for neighbors; for distant hexes, the
 * direction closest in angle (largest cube dot product; ties go to the lower index).
 * Returns -1 when a equals b.
 */
export function directionTo(a: Hex, b: Hex): Direction | -1 {
  if (hexEquals(a, b)) return -1;
  const d = toCube(subtract(b, a));
  let best: Direction = 0;
  let bestDot = -Infinity;
  for (let i = 0; i < 6; i++) {
    const v = toCube(DIRECTIONS[i] as Hex);
    const dot = d.q * v.q + d.r * v.r + d.s * v.s;
    if (dot > bestDot) {
      bestDot = dot;
      best = i as Direction;
    }
  }
  return best;
}

/** Index of `b` among `a`'s neighbors, or -1 if not adjacent. */
export function neighborDirection(a: Hex, b: Hex): Direction | -1 {
  const dq = b.q - a.q;
  const dr = b.r - a.r;
  for (let i = 0; i < 6; i++) {
    const d = DIRECTIONS[i] as Hex;
    if (d.q === dq && d.r === dr) return i as Direction;
  }
  return -1;
}

// ---------- integer hex line ----------

// The line is sampled at N+1 evenly spaced points from a to b (N = distance) and each point
// is rounded to the nearest hex. Everything is scaled by N·SCALE so it stays integer.
// Every sample is shifted by a tiny fixed offset NUDGE (sum 0, far below one step), so no sample
// ever lies exactly on a hex border: the same pair always produces the same line on every engine,
// and hexLine(b, a) is hexLine(a, b) reversed.
const SCALE = 8;
const NUDGE: Cube = { q: 1, r: 2, s: -3 };

/** Round a scaled cube point (each component divided by `den`) to the nearest hex. */
function roundScaled(x: number, y: number, z: number, den: number): Cube {
  const half = den / 2; // den is always even here
  let q = floorDiv(x + half, den);
  let r = floorDiv(y + half, den);
  let s = floorDiv(z + half, den);
  const dq = absInt(q * den - x);
  const dr = absInt(r * den - y);
  const ds = absInt(s * den - z);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  else s = -q - r;
  return { q, r, s };
}

/** Hexes on the straight line from `a` to `b`, inclusive, in order. Length = distance + 1. */
export function hexLine(a: Hex, b: Hex): Hex[] {
  const n = distance(a, b);
  if (n === 0) return [{ q: a.q, r: a.r }];
  const ca = toCube(a);
  const cb = toCube(b);
  const den = n * SCALE;
  const out: Hex[] = [];
  for (let i = 0; i <= n; i++) {
    const x = ca.q * den + (cb.q - ca.q) * i * SCALE + NUDGE.q;
    const y = ca.r * den + (cb.r - ca.r) * i * SCALE + NUDGE.r;
    const z = ca.s * den + (cb.s - ca.s) * i * SCALE + NUDGE.s;
    out.push(fromCube(roundScaled(x, y, z, den)));
  }
  return out;
}

// ---------- rings and ranges ----------

/** All hexes within `radius` of `center`, sorted by compareHex. */
export function hexesInRange(center: Hex, radius: number): Hex[] {
  const out: Hex[] = [];
  for (let dq = -radius; dq <= radius; dq++) {
    const lo = Math.max(-radius, -dq - radius);
    const hi = Math.min(radius, -dq + radius);
    for (let dr = lo; dr <= hi; dr++) out.push({ q: center.q + dq, r: center.r + dr });
  }
  return out.sort(compareHex);
}
